/**
 * ENGASJEMENTET (grep F1, sak 253.5, analysen av 102): én status i ett ordforråd, lest likt av
 * bookinglista, Forespørsler, Konserter, Mine konserter, Lineup, kalenderen og eksporten.
 *
 * Tilstandsmaskinen bor i basen (0927). Her står ordforrådet, rangen og den ene regelen skjermene
 * trenger for å vise riktig knapp — serveren håndhever den samme regelen uansett.
 */
import { z } from 'zod';
import { wireInstant } from './wire-time.js';

/** Hovedløpet, i rekkefølge, og de to utgangene (253.70: «Falt bort», og «Avlyst» etter avtale). */
export const ENGAGEMENT_STATUSES = [
  'not_asked',
  'asked',
  'answered',
  'bid',
  'verbally_confirmed',
  'signed',
  'played',
  'fell_through',
  'cancelled',
] as const;
export type EngagementStatus = (typeof ENGAGEMENT_STATUSES)[number];

/** Undergrunnen for «Falt bort». */
export const FELL_THROUGH_REASONS = [
  'agency_declined',
  'unavailable',
  'we_withdrew',
  'hold_expired',
  'date_taken',
] as const;
export type FellThroughReason = (typeof FELL_THROUGH_REASONS)[number];

/** Det brukeren selv kan sette (253.72). Resten flytter systemet ved hendelser. */
export const ENGAGEMENT_USER_STATUSES = [
  'verbally_confirmed',
  'signed',
  'fell_through',
  'cancelled',
] as const satisfies readonly EngagementStatus[];
export type EngagementUserStatus = (typeof ENGAGEMENT_USER_STATUSES)[number];

const RANK: Readonly<Record<EngagementStatus, number | null>> = {
  not_asked: 0,
  asked: 1,
  answered: 2,
  bid: 3,
  verbally_confirmed: 5,
  signed: 6,
  played: 7,
  fell_through: null,
  cancelled: null,
};

/** Rangen i hovedløpet; utgangene har ingen. Samme tall som `app.engagement_rank` i basen. */
export function engagementRank(status: EngagementStatus): number | null {
  return RANK[status];
}

/** Avtalt: Muntlig bekreftet, Signert eller Gjennomført. */
export function engagementIsAgreed(status: EngagementStatus): boolean {
  const rank = RANK[status];
  return rank !== null && rank >= 5;
}

/**
 * KAN BRUKEREN SETTE DENNE STATUSEN NÅ? Samme regel som `app.engagement_user_may` (0927):
 * Muntlig bekreftet og Signert bare framover og bare når ingen bud i Tvitvi eier svaret; Falt bort
 * før avtale; Avlyst etter avtale. Skjermen viser knappen, basen avgjør.
 */
export function engagementUserMay(
  from: EngagementStatus,
  to: EngagementUserStatus,
  ownedByBid: boolean,
): boolean {
  const rank = RANK[from];
  switch (to) {
    case 'verbally_confirmed':
      return (from === 'fell_through' || (rank !== null && rank < 5)) && !ownedByBid;
    case 'signed':
      return (from === 'fell_through' || (rank !== null && rank < 6)) && !ownedByBid;
    case 'fell_through':
      return from === 'fell_through' || (rank !== null && rank < 5);
    case 'cancelled':
      return from === 'verbally_confirmed' || from === 'signed';
  }
}

/** Engasjementet slik én rad bærer det: på bud, forespørsel, programpost, konsert og gigg. */
export const engagementRefDtoSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ENGAGEMENT_STATUSES),
  fellThroughReason: z.enum(FELL_THROUGH_REASONS).nullable(),
  /** Merket for hold (253.71): datoen det holdes til. */
  holdUntil: z.string().nullable(),
  /** 1. hold, 2. hold … (253.74). Bygges ikke ennå; byråets «Holdt» uten dato står som 1. */
  holdRank: z.number().int().nullable(),
  statusChangedAt: wireInstant,
});
export type EngagementRefDto = z.infer<typeof engagementRefDtoSchema>;

/** Hold er et merke som vises som status når ingenting sterkere finnes (253.71). */
export function engagementOnHold(ref: Pick<EngagementRefDto, 'holdUntil' | 'holdRank'>): boolean {
  return ref.holdUntil !== null || ref.holdRank !== null;
}

/** `PATCH /v1/engagements/:id/status` — det brukeren kan sette (253.72). */
export const setEngagementStatusRequestSchema = z
  .object({
    status: z.enum(ENGAGEMENT_USER_STATUSES),
    /** Påkrevd for «Falt bort», og bare der. */
    reason: z.enum(FELL_THROUGH_REASONS).nullable().optional(),
  })
  .strict()
  .refine((body) => (body.status === 'fell_through') === (body.reason != null), {
    message: 'reason is required for fell_through, and only there',
    path: ['reason'],
  })
  .meta({ id: 'SetEngagementStatusRequest' });
export type SetEngagementStatusRequest = z.infer<typeof setEngagementStatusRequestSchema>;

/**
 * PORTEN FOR DET MOTPARTEN EIER (grep F3, 0930 `app.engagement_facts`). Lest der det står, aldri
 * kopiert inn: agenten byrået har satt på artisten (83), byen og kapasiteten (114), formatet og
 * antallet reisende (90), og tiden og lengden fra plasseringen (93). Ingen beløp — porten har ingen
 * pengekolonne, så arrangørens tall kan ikke nå byrået gjennom den.
 */
export const ENGAGEMENT_TIME_SOURCES = ['performance', 'programming', 'bid'] as const;
export type EngagementTimeSource = (typeof ENGAGEMENT_TIME_SOURCES)[number];

export const engagementFactsDtoSchema = z
  .object({
    agent: z
      .object({
        personId: z.string().uuid(),
        name: z.string(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        agencyName: z.string(),
        /** Byråets generelle bookingkontakt (0945), ikke en agent satt på artisten. */
        general: z.boolean().default(false),
      })
      .nullable(),
    city: z.string().nullable(),
    capacity: z.number().int().nullable(),
    format: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    travellingParty: z.number().int().nullable(),
    /**
     * Når saken ble bekreftet og format og antall ble et øyeblikksbilde (0945, QA13 90). Null mens
     * de leses levende fra svaret.
     */
    confirmedAt: wireInstant.nullable().default(null),
    /** `HH:MM` i arrangementets sone. */
    startsAt: z.string().nullable(),
    setLengthMinutes: z.number().int().nullable(),
    /** Hvor tiden kom fra: programposten, linja i programmeringen eller budet. */
    timeSource: z.enum(ENGAGEMENT_TIME_SOURCES).nullable(),
  })
  .meta({ id: 'EngagementFacts' });
export type EngagementFactsDto = z.infer<typeof engagementFactsDtoSchema>;

/**
 * `GET /v1/engagements/:id` — engasjementet med det skjermen trenger for å vise riktig knapp:
 * om et bud i Tvitvi eier svaret, og hvilke statuser den som spør kan sette nå (tom for motparten,
 * som bare leser). `facts` er porten over.
 */
export const engagementDtoSchema = engagementRefDtoSchema
  .extend({
    side: z.enum(['owner', 'counterparty']),
    ownedByBid: z.boolean(),
    settable: z.array(z.enum(ENGAGEMENT_USER_STATUSES)),
    facts: engagementFactsDtoSchema.nullable(),
    /**
     * QA16 funn 18: trinnene før budet som saken faktisk har gått gjennom. Et direkte bud har
     * ingen, og stigen viser da ikke «Spurt» og «Svart». Valgfri for eldre klienter.
     */
    path: z.array(z.enum(['asked', 'answered'])).optional(),
  })
  .meta({ id: 'Engagement' });
export type EngagementDto = z.infer<typeof engagementDtoSchema>;
